import Dexie, { Table } from "dexie";
import {
  AudioSegment,
  ChunkIndexRow,
  DiagnosticEvent,
  PayloadRecord,
  RecoveryCheckpoint,
  SessionManifest,
} from "../domain/types";

const CONSULTATION_DB_NAME = "sofya.transcription.consultation";

export class ConsultationDexieDb extends Dexie {
  manifests!: Table<SessionManifest, string>;
  segments!: Table<AudioSegment, number>;
  payloads!: Table<PayloadRecord, string>;
  recoveryCheckpoints!: Table<RecoveryCheckpoint, string>;
  chunkIndex!: Table<ChunkIndexRow, [string, number]>;
  diagnostics!: Table<DiagnosticEvent, number>;

  constructor() {
    super(CONSULTATION_DB_NAME);

    this.version(1).stores({
      manifests:
        "&sessionId, streamEpoch, updatedAt, archiveState, lastDurableSeq, hardCommittedMs, softCommittedMs",
      segments:
        "++id, [sessionId+segmentId], [sessionId+startSeq], [sessionId+endSeq], [sessionId+startMs], [sessionId+endMs], [sessionId+keepForReplay], [sessionId+keepForArchive], createdAt, deletedAt",
      payloads: "&payloadKey, sessionId, segmentId, byteLength, checksum32",
      recoveryCheckpoints:
        "&sessionId, hardCommittedMs, softCommittedMs, lastCommitAdvanceAt",
      chunkIndex: "[sessionId+seq], [sessionId+endMs], [sessionId+segmentId]",
      diagnostics: "++id, sessionId, ts, code",
    });
  }
}

let databaseInstance: ConsultationDexieDb | null = null;
let cleanupTimer: ReturnType<typeof setTimeout> | null = null;

export const getConsultationDb = (): ConsultationDexieDb => {
  if (!databaseInstance) {
    databaseInstance = new ConsultationDexieDb();
  }

  return databaseInstance;
};

const clearConsultationDbStores = async (
  db: ConsultationDexieDb,
  shouldCloseAfterUse: boolean
): Promise<void> => {
  try {
    await (db.transaction as any)(
      "rw",
      db.manifests,
      db.segments,
      db.payloads,
      db.recoveryCheckpoints,
      db.chunkIndex,
      db.diagnostics,
      async () => {
        await Promise.all([
          db.manifests.clear(),
          db.segments.clear(),
          db.payloads.clear(),
          db.recoveryCheckpoints.clear(),
          db.chunkIndex.clear(),
          db.diagnostics.clear(),
        ]);
      }
    );
  } catch (error) {
    if ((error as { name?: string })?.name !== "NoSuchDatabaseError") {
      throw error;
    }
  } finally {
    if (shouldCloseAfterUse) {
      db.close();
    }
  }
};

export const dropConsultationDb = async (): Promise<void> => {
  if (cleanupTimer) {
    clearTimeout(cleanupTimer);
    cleanupTimer = null;
  }

  const dbToClear = databaseInstance ?? new ConsultationDexieDb();
  const shouldCloseAfterUse = !databaseInstance;
  databaseInstance = null;

  await clearConsultationDbStores(dbToClear, shouldCloseAfterUse);

  if (!shouldCloseAfterUse) {
    dbToClear.close();
  }
};

export const scheduleConsultationDbDrop = (ttlMs: number): void => {
  if (cleanupTimer) {
    clearTimeout(cleanupTimer);
  }

  cleanupTimer = setTimeout(() => {
    void dropConsultationDb();
  }, ttlMs);
};

export const cancelConsultationDbDrop = (): void => {
  if (cleanupTimer) {
    clearTimeout(cleanupTimer);
    cleanupTimer = null;
  }
};
